/*
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 UNESP Universidade Estadual Paulista "Júlio de Mesquita Filho"
 * Copyright 2024-2026 UNICAMP Universidade Estadual de Campinas
 *
 */

import { HttpErrorResponse } from '@angular/common/http'
import { Component, ViewChild, Input, OnInit, ElementRef} from '@angular/core'
import { AbstractControl, FormControl, ValidationErrors, ValidatorFn } from '@angular/forms'
import { MatDialog } from '@angular/material/dialog'
import { UnespCoreMessageService } from 'src/libs/unesp-core'
import { ArquivoUploadStatus } from 'src/app/models/arquivo-upload-status.model'
import { ArquivoUploadService } from 'src/app/services/arquivo-upload.service'
import { DownloadService } from 'src/app/services/download.service'
import { ArquivoViewerDialogComponent } from '../arquivo-viewer-dialog/arquivo-viewer-dialog.component'
import { ArquivoPreviewDialogComponent } from '../arquivo-preview-dialog/arquivo-preview-dialog.component'
import { ConsultaLogService } from 'src/app/services/consulta-log.service'
import { InscricaoComplementoService } from 'src/app/services/inscricao-complemento.service'
import { firstValueFrom } from 'rxjs'
import { environment } from 'src/environments/environment'

export enum ArquivoStatus {
  VAZIO,
  ENVIANDO,
  SELECIONADO,
  ENVIADO,
  SALVO,
  ERRO,
}

export class ArquivoSelecionado {
  name: string = ''
  uuid?: string
  size: number = 0
  status: ArquivoStatus = ArquivoStatus.VAZIO
  file: File | null = null
  downloading?: boolean
  downloadProgress?: number

  setNameAndSize(name: string, size: number) {
    if (!name) return
    this.name = name.toLowerCase()
    this.uuid = name.split('.', 2)[0]
    this.size = size
    this.file = null
    this.status = ArquivoStatus.SALVO
  }

  setFile(file: File | null) {
    if (file == null) return
    this.file = file
    this.size = file.size
    this.name = file.name
    this.status = ArquivoStatus.SELECIONADO
  }

  toString(): string {
    return this.name
  }
}

export function arquivoUploadValidator(required: boolean): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const arquivo = control.value as ArquivoSelecionado
    if (!arquivo) return null

    if (required && arquivo.status == ArquivoStatus.VAZIO) {
      return { required: true }
    }

    switch (arquivo.status) {
      case ArquivoStatus.ERRO:
        return { upload_error: true }
      case ArquivoStatus.VAZIO:
      case ArquivoStatus.SALVO:
      case ArquivoStatus.ENVIADO:
      case ArquivoStatus.SELECIONADO:
        return null
      default:
        return { uploading: true }
    }
  }
}

@Component({
  selector: 'app-arquivo-upload',
  templateUrl: './arquivo-upload.component.html',
  styleUrls: ['./arquivo-upload.component.scss'],
})
export class ArquivoUploadComponent implements OnInit {
  @Input() fc: FormControl<ArquivoSelecionado> = new FormControl()
  @Input() label: string = 'Arquivo'
  @Input() viewerTitle?: string
  @Input() tip: string = 'Selecione um arquivo para Upload'
  @Input() accept: string = '*'
  @Input() autoUpload: boolean = false
  @Input() usarArquivoViewer: boolean = false
  @Input() uploadPath?: string
  @Input() appendData?: Map<string, any>  
  @Input() idInscricao?: number
  @Input() complementoId?: number | string
  @Input() idConcurso?: string
  @Input() codigoOrgao?: string
  @ViewChild('fileInput') fileInput: ElementRef | undefined

  disabled: boolean = false
  required: boolean = false

  constructor(
    private arquivoUploadService: ArquivoUploadService,
    private downloadService: DownloadService,
    private unespCoreMessageService: UnespCoreMessageService,
    public dialog: MatDialog,
    private inscricaoComplementoService: InscricaoComplementoService,
    private consultaLogService: ConsultaLogService
  ) {}

  ngOnInit(): void {
    if (this.fc.disabled) this.disabled = true
    this.fc.registerOnDisabledChange((isDisabled: boolean) => (this.disabled = isDisabled))

    const validator = this.fc.validator ? this.fc.validator({} as AbstractControl) : null
    if (validator && validator['required']) this.required = true
  }

  upload(): Promise<ArquivoUploadStatus> | null {
    if (
      this.fc.value.file &&
      this.fc.value.status == ArquivoStatus.SELECIONADO &&
      typeof this.uploadPath != 'undefined'
    )
      return this.upload_async()

    return null
  }
  /**
   * Carrega arquivo em backend
   */
  private async upload_async(): Promise<ArquivoUploadStatus> {
    let formData = new FormData()

    if (this.appendData) for (let [key, value] of this.appendData) formData.append(key, value)

    formData.append('arquivo', this.fc.value.file!)
    this.fc.value.status = ArquivoStatus.ENVIANDO

    var promise = new Promise<ArquivoUploadStatus>((resolve, reject) => {
      this.arquivoUploadService.arquivoUpload(this.uploadPath!, formData).subscribe({
        next: (r: ArquivoUploadStatus) => {
          Object.assign(this.fc.value, {
            status: ArquivoStatus.ENVIADO,
            name: r.arquivoNome,
            uuid: r.uuid!,
            size: r.size,
          })
          if (r.uploadPath) this.uploadPath = r.uploadPath
          this.fc.setValue(this.fc.value)
          resolve(r)
        },
        error: err => {
          this.fc.value.status = ArquivoStatus.ERRO
          this.fc.setValue(this.fc.value)
          if (err instanceof HttpErrorResponse) {
            if (err.status == 413) {
              let erro: { mensagemUsuario: string; mensagemDesenvolvedor: string } = err.error
              this.unespCoreMessageService.showMessageError(erro.mensagemUsuario)
            } else {
              this.unespCoreMessageService.showMessageError(err.message)
            }
          } else {
            console.error(err)
            reject()
          }
        },
      })
    })

    const v = await promise
    return v
  }

  selecionaArquivo(e: Event) {
    const target = e.target as HTMLInputElement
    const files = target.files as FileList
    const file = files.item(0)

    if (file == null) return

    if (this.accept != '*' && !this.accept.split(',').includes(file.type)) {
      this.unespCoreMessageService.showMessageError(`Formato de arquivo invalido, use: ${this.accept}`)
      target.value = ''
      return
    }

    const dialogRef = this.dialog.open(ArquivoPreviewDialogComponent, {
      data: { file, titulo: this.viewerTitle || this.label },
      width: '80%',
      height: '90%',
      panelClass: 'fullheight-dialog',
      disableClose: true,
    })

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.fc.value.setFile(file)
        this.fc.setValue(this.fc.value)
        this.fc.markAsDirty()
        if (this.autoUpload && this.fc.value.status == ArquivoStatus.SELECIONADO && typeof this.uploadPath != 'undefined')
          this.upload_async()
      } else {
        this.consultaLogService.logUploadPreviewCancelado({
          nome: file.name, tipo: file.type, tamanho: file.size, tela: this.label,
        })
      }
      target.value = ''
    })
  }

  fileSelectorClick() {
    if (this.fc.value.downloading) return
    
    this.fileInput?.nativeElement.click()
  }

  download() {
    if (this.fc.value.downloading) return
    
    if (this.fc.value.status == ArquivoStatus.ENVIADO || this.fc.value.status == ArquivoStatus.SALVO) {
      if (!this.uploadPath) return
      
      const shouldUseChunks = environment.usarDownloadChunks && this.fc.value.size > Number(environment.minDownloadForChunk) * 1024 * 1024
      
      if (shouldUseChunks && this.idInscricao && this.complementoId && this.idConcurso && this.codigoOrgao) {
        this.abrirArquivoViewerViaChunks()
      } else if (this.usarArquivoViewer) {
        this.abrirArquivoViewer()
      } else {
        this.downloadService.arquivoDownload(this.uploadPath, this.fileName())
      }
    }
  }

  async abrirArquivoViewerViaChunks() {
    this.fc.value.downloading = true
    this.fc.value.downloadProgress = 0
    
    try {
      const chunkedDownload = await firstValueFrom(
        this.inscricaoComplementoService
          .obterUrlsDownloadChunks(this.idInscricao!, Number(this.complementoId!), this.idConcurso!, this.codigoOrgao!)
      )
      
      if (chunkedDownload) {        
        const url = await this.downloadService.getObjectURLViaChunks(chunkedDownload, (progress) => {
          this.fc.value.downloadProgress = progress
        })
        
        this.dialog.open(ArquivoViewerDialogComponent, {
          data: { tituloDoArquivo: this.viewerTitle || this.label, urlDoArquivo: url },
          width: '80%',
          height: '90%',
          panelClass: 'fullheight-dialog',
        })
      }
    } catch (error) {
      console.error('Erro no download via chunks:', error)
    } finally {
      this.fc.value.downloading = false
      this.fc.value.downloadProgress = undefined
    }
  }

  private fileName(): string {
    if (this.fc.value.file == null && this.fc.value.uuid) return this.fc.value.name
    else return this.fc.value.uuid + '.' + this.fc.value.name.split('.').pop()
  }

  private abrirArquivoViewer() {
    this.downloadService.arquivoObjectURLPorPath(this.uploadPath!, this.fileName()).subscribe(url =>
      this.dialog.open(ArquivoViewerDialogComponent, {
        data: { tituloDoArquivo: this.viewerTitle || this.label, urlDoArquivo: url },
        width: '80%',
        height: '90%',
        panelClass: 'fullheight-dialog',
      })
    )
  }
}
